import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { ParLevelRow } from '@/types/kitchenFlowPurchasing'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { fmtQty } from '@/lib/quantity'
import { parInputError } from './purchasingLogic'
import { mergeDrafts } from './purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.par

type Draft = Record<string, { min: string; max: string }>

const toDraft = (rows: ParLevelRow[]): Draft =>
  Object.fromEntries(rows.map((r) => [r.location_id, { min: r.min_qty != null ? String(r.min_qty) : '', max: r.max_qty != null ? String(r.max_qty) : '' }]))

/** Kitchen flow lane D: min / max per location in the item drawer. Replaces
 *  nothing — the single "Warn at" reorder level stays next to it. */
export function ParLevelsCard({ itemId, unit, showToast }: {
  itemId: string
  unit: string
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const { data, isError, refetch } = useQuery({
    queryKey: ['parLevels', itemId],
    queryFn: () => apiClient.getParLevels(itemId).then((r) => r.data ?? []),
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
  })
  const rows = data ?? []
  const [draft, setDraft] = useState<Draft>({})
  // Pilot Batch 5: a refetch (another tab saved, a receipt landed) refreshes
  // only the rows nobody is editing — a half-typed level is never wiped.
  const lastServer = useRef<Draft>({})
  useEffect(() => {
    if (!data) return
    const fresh = toDraft(data)
    setDraft((cur) => mergeDrafts(cur, lastServer.current, fresh, (a, b) => a.min === b.min && a.max === b.max))
    lastServer.current = fresh
  }, [data])

  const errors = Object.fromEntries(rows.map((r) => [r.location_id, parInputError(draft[r.location_id]?.min ?? '', draft[r.location_id]?.max ?? '')]))
  const dirty = rows.some((r) => {
    const d = draft[r.location_id]
    const orig = toDraft([r])[r.location_id]
    return d && (d.min !== orig.min || d.max !== orig.max)
  })
  const hasError = Object.values(errors).some(Boolean)

  const save = useMutation({
    mutationFn: () =>
      apiClient.putParLevels(
        itemId,
        rows.map((r) => {
          const d = draft[r.location_id] ?? { min: '', max: '' }
          const blank = !d.min.trim() && !d.max.trim()
          return {
            location_id: r.location_id,
            min_qty: blank ? null : d.min.trim() ? Number(d.min) : 0,
            max_qty: blank || !d.max.trim() ? null : Number(d.max),
          }
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parLevels', itemId] })
      qc.invalidateQueries({ queryKey: ['orderSuggestions'] })
      showToast('success', S.saved)
    },
    onError: (e: Error) => showToast('error', e.message),
  })

  if (isError && rows.length === 0) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border p-3 text-sm">
        <span className="text-danger-fg">{S.title}: {PURCHASING_STRINGS.errors.load}</span>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>{PURCHASING_STRINGS.errors.retry}</Button>
      </div>
    )
  }
  if (rows.length === 0) return null
  const errText = (e: string | null) =>
    e === 'negative' ? S.errNegative : e === 'max_below_min' ? S.errMaxBelowMin : e === 'not_a_number' ? S.errNaN : null

  return (
    <div className="rounded-lg border p-3">
      <h4 className="text-sm font-semibold">{S.title}</h4>
      <p className="mb-2 text-xs text-muted-foreground">{S.help}</p>
      <div className="space-y-2">
        {rows.map((r) => {
          const d = draft[r.location_id] ?? { min: '', max: '' }
          const err = errText(errors[r.location_id])
          return (
            <div key={r.location_id}>
              <div className="grid grid-cols-[1fr_5.5rem_5.5rem] items-end gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{r.location_name}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">{S.onHand}: {fmtQty(r.on_hand)} {unit}</p>
                </div>
                <label className="text-xs text-muted-foreground">
                  {S.least}
                  <Input
                    inputMode="decimal" className="tabular-nums" value={d.min}
                    onChange={(e) => setDraft((x) => ({ ...x, [r.location_id]: { ...d, min: e.target.value } }))}
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  {S.most}
                  <Input
                    inputMode="decimal" className="tabular-nums" value={d.max}
                    onChange={(e) => setDraft((x) => ({ ...x, [r.location_id]: { ...d, max: e.target.value } }))}
                  />
                </label>
              </div>
              {err && <p className="mt-1 text-xs font-medium text-danger-fg">{err}</p>}
            </div>
          )
        })}
      </div>
      {dirty && (
        <div className="mt-3 flex justify-end">
          <Button disabled={hasError || save.isPending} onClick={() => save.mutate()}>
            {S.save}
          </Button>
        </div>
      )}
    </div>
  )
}
